import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api, { setAuthToken, setUnauthorizedHandler } from '../api';

const AuthContext = createContext(null);

// sessionStorage, deliberately, not localStorage and not memory.
//
// Memory-only meant every refresh or deep link threw the user back to the
// login screen. The reasoning was XSS, but the protection is thin: script
// running in the page can issue authenticated requests through the app
// itself whatever holds the token, so the cost (constant re-logins) bought
// very little.
//
// localStorage would be the other extreme - a session surviving indefinitely
// on a shared office machine, which is a real scenario for a CA firm.
// sessionStorage is scoped to the tab: it survives refreshes and deep links,
// and is gone when the tab or browser closes.
const STORAGE_KEY = 'needcfo.session';

function readStoredSession() {
  // Private-browsing modes and locked-down browsers can throw on access
  // rather than just returning null, so never let this break app startup.
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { token: null, user: null };
    const parsed = JSON.parse(raw);
    return { token: parsed.token || null, user: parsed.user || null };
  } catch {
    return { token: null, user: null };
  }
}

function writeStoredSession(token, user) {
  try {
    if (token) sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ token, user }));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable - the session still works for this page view,
    // it just won't survive a refresh. Better than failing to log in.
  }
}

// Read once at module load, and hand the token to axios immediately. Doing
// this in an effect instead would leave a window where a mounted screen fires
// its first request before the Authorization header exists - that request
// 401s, the global handler logs the user straight back out, and the refresh
// looks broken in a different way.
const restored = readStoredSession();
setAuthToken(restored.token);

export function AuthProvider({ children }) {
  // Seeded from storage in the initializer rather than in an effect, so the
  // very first render already knows it is authenticated - otherwise the app
  // paints the login screen for a frame and route guards can bounce a
  // logged-in user before the token lands.
  const [token, setToken] = useState(restored.token);
  const [user, setUser] = useState(restored.user);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    setAuthToken(token);
  }, [token]);

  useEffect(() => {
    writeStoredSession(token, user);
  }, [token, user]);

  const logout = useCallback(() => {
    if (token) {
      api.post('/auth/logout').catch(() => {});
    }
    setToken(null);
    setUser(null);
  }, [token]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setToken(null);
      setUser(null);
    });
  }, []);

  // Re-check a restored session against the server once on startup. The stored
  // user object is only ever a render hint - anything that matters is enforced
  // server-side - but taking the server's copy means a role or name changed
  // since the tab was opened is reflected, and an expired token is cleared
  // right away instead of surfacing as a 401 on whatever the user clicks
  // first. A 401 here is handled by the global handler above.
  useEffect(() => {
    if (!restored.token) return;
    api
      .get('/auth/me')
      .then((res) => setUser(res.data))
      .catch(() => {});
  }, []);

  const login = useCallback((email, password) => {
    setAuthError(null);
    return api
      .post('/auth/login', { email, password })
      .then((res) => {
        setToken(res.data.access_token);
        setUser(res.data.user);
        return res.data.user;
      })
      .catch((err) => {
        const message = err.response?.data?.detail || 'Login failed';
        setAuthError(message);
        throw err;
      });
  }, []);

  // Used by the OTP verification screen, which gets back the exact same
  // {access_token, user} shape as /auth/login from a different endpoint -
  // no need for a second round-trip login call once OTP is confirmed.
  const applySession = useCallback((accessToken, sessionUser) => {
    setToken(accessToken);
    setUser(sessionUser);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        isAuthenticated: !!token,
        authError,
        login,
        logout,
        applySession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
